// What a player has actually met (§16.2).
//
// The curriculum in 11b offers one concept a turn, and it can only do that if
// something knows which concepts this person has already used. That is this
// file: a pure reduction from a command and the states either side of it to
// the mechanics it demonstrates.
//
// **A command is not a mechanic**, and the gap is the whole reason this takes
// `before` and `after` rather than just the command:
//
//   - **Commands issued** are the easy third. `lay-belt-line` is belts,
//     `forge` is minting, `re-aim` is facing.
//   - **Outcomes observed** are the interesting two thirds and nobody issues
//     them: a refinery finishing a batch, a lane delivering into a machine, a
//     heap running dry, a part being crushed. Knowing somebody clicked "mine"
//     is not knowing they have ever watched a lane deliver, and it is the
//     second that teaches the economy.
//   - **Gestures with no command at all** — turning the board, opening a
//     badge, reading the rules — are the third source and are *not* here. No
//     command will ever record them, so the UI counts them directly and this
//     file stays pure.
//
// **`src/engine/rules/` may never import any of this**, and that is not negotiable
// (§16.2.2). A rule that consulted "how often has this person mined" would
// derive two different games from one log, and the hash canary would fire with
// no bug to find. Nothing here is game state; it is a reading *of* game state.
//
// It shares its shape with `scorecard.ts` on purpose — derived rather than
// recorded, because a log replays to exactly one sequence of states — and the
// consequence is that a profile **backfills**: counters added today can be
// recomputed over logs written before they existed.

import { applyCommand, type Command, type GameState, newGame } from "src/engine/rules/commands";
import { isCapture, isEnPassant } from "src/engine/rules/move";
import { beltDirAt, heapAt, NO_BELT } from "src/engine/rules/industry/state";

/**
 * Every mechanic the game can teach, as ids a curriculum can name.
 *
 * Concepts rather than command names: `lay-belt`, `lay-belt-line` and
 * `lay-belt-path` are three gestures for one idea, and a player who has laid a
 * lane has learned it however they did it. The ones that *are* one-to-one with
 * a command are the ones that are genuinely their own idea — re-aiming a pawn
 * is not laying a belt.
 *
 * Order is the order they are declared and nothing reads it as a ranking; the
 * curriculum's ordering is `needs`, in its own table (§16.3.1).
 */
export const MECHANICS = [
  // The chess half.
  "move",
  "capture",
  "promote",
  // Direction, which is this game's first unfamiliar idea.
  "re-aim",
  // Getting material out of the ground and into something.
  "mine",
  "handoff",
  // The lane: laying one, aiming it, and taking it up again.
  "belts",
  "belt-aim",
  "belt-remove",
  // What a factory does once it is fed. None of these is ever *issued*.
  "deliver",
  "refine",
  "crush",
  "heap-dry",
  // Spending it.
  "forge",
  "recipe",
  // Telling a machine what to do with what it holds.
  "dump",
  "handoff-rate",
  "king-task",
  // Buildings.
  "build",
  "demolish",
  "store-policy",
  // Holding stock back.
  "reserve",
  // The run's own vocabulary.
  "card",
  "power",
  "sacrifice"
] as const;

export type MechanicId = (typeof MECHANICS)[number];

/**
 * Bumped whenever `MECHANICS` or `mechanicsOf` changes what it counts.
 *
 * A stored profile is a cache of a reduction, and the reduction has just
 * changed — so the number is what tells a loader to throw the cache away and
 * backfill rather than to trust counts that mean something slightly different.
 * The alternative is a profile that silently mixes two definitions, which is
 * the failure mode a version number exists to prevent and which is invisible
 * without one.
 */
// 2: `repair` and `march` retired with knight repair and standing orders (§31 R1, R4).
export const MECHANICS_VERSION = 2;

/** Commands that are one concept each, however they were issued. */
const BY_COMMAND: Partial<Record<Command["type"], MechanicId>> = {
  "re-aim": "re-aim",
  mine: "mine",
  "load-belt": "handoff",
  "lay-belt": "belts",
  "lay-belt-line": "belts",
  "lay-belt-path": "belts",
  "rotate-belt": "belt-aim",
  "toggle-belt": "belt-aim",
  "remove-belt": "belt-remove",
  forge: "forge",
  "set-target": "recipe",
  "set-dump": "dump",
  "set-handoff": "handoff-rate",
  "set-king-task": "king-task",
  build: "build",
  demolish: "demolish",
  "set-store-policy": "store-policy",
  "set-reserve": "reserve",
  "play-card": "card",
  // The Time Fluxxer, which is a card being played however little it looks like one
  // here — it never reaches `play-card`, because a rewind is an operation on
  // the log. Without this row a player who only ever played that card would
  // never count as having used one, and the curriculum would keep offering to
  // explain the hand. **No `MECHANICS_VERSION` bump**: the command did not
  // exist before, so no stored log can contain one and the reduction over
  // every profile already written is unmoved.
  "burn-card": "card",
  // A trophy (§31 A2) is the hand's door: new, so no stored log holds one.
  "claim-trophy": "card",
  "keep-trophy": "card",
  "set-power": "power",
  // §31 A4: new, so no stored log holds one.
  seize: "power",
  sacrifice: "sacrifice"
};

/** How much scrap is lying about, so a heap running dry can be noticed. */
function heapTotal(state: GameState): number {
  let total = 0;
  for (const sq of state.position.cellList) total += heapAt(state.industry, sq);
  return total;
}

/** How many parts are on the board, for telling a crush from a demolition. */
function beltTotal(state: GameState): number {
  let parts = 0;
  for (const sq of state.position.cellList) {
    if (beltDirAt(state.industry, sq) !== NO_BELT) parts++;
  }
  return parts;
}

/**
 * What this command taught, if anything.
 *
 * Returns ids rather than incrementing a counter, so the caller decides where
 * the tally goes — telemetry aggregates per *game* on the server, the profile
 * aggregates per *player* in the browser, and one function serves both. Keeping
 * the two stores apart is what stops this quietly becoming a tracking system.
 *
 * Never more than a handful, and duplicates are removed: a turn that mines and
 * delivers has met two concepts, not five.
 */
export function mechanicsOf(
  before: GameState,
  command: Command,
  after: GameState
): MechanicId[] {
  const met = new Set<MechanicId>();

  const named = BY_COMMAND[command.type];
  if (named) met.add(named);

  if (command.type === "move") {
    met.add("move");
    // Read off the move record rather than the board: an en passant's victim
    // is not standing on the square that was taken, which is the mistake
    // `tally.ts` records having made once already.
    const played = after.history[after.history.length - 1];
    if (played && (isCapture(played) || isEnPassant(played))) met.add("capture");
    if (played && played.promotion) met.add("promote");
  }

  // The outcomes. `lastTick` is the tick's own report of what it did, which is
  // the only thing that can answer these — a merge and an arrival leave the
  // same board, so nothing here can be worked out by comparing squares.
  const tick = after.lastTick;
  if (tick && tick !== before.lastTick) {
    if (tick.cooked > 0) met.add("refine");
    // A machine taking material off a lane. The pass is tagged, so this is the
    // difference between "cargo moved" and "a lane delivered into something",
    // which is the one a player has to see before belts mean anything.
    if (tick.cargo?.some((move) => move.phase === "grab")) met.add("deliver");
    if (tick.mined > 0) met.add("mine");
  }

  // A heap running dry: the ore was there before and is gone now. Worth its own
  // concept because it is the moment the economy stops being free.
  if (heapTotal(before) > 0 && heapTotal(after) === 0) met.add("heap-dry");

  // A part that went away without being asked to go. `remove-belt` is the
  // player taking one up and is already counted as its own idea; this is the
  // opponent doing it to them, which is the thing that teaches that lanes are
  // contested ground.
  if (command.type !== "remove-belt" && beltTotal(after) < beltTotal(before)) {
    met.add("crush");
  }

  return [...met];
}

/** How often each mechanic has been met. Absent means never. */
export type MechanicCounts = Partial<Record<MechanicId, number>>;

/** Add one command's worth of mechanics to a tally, in place. */
export function tally(counts: MechanicCounts, met: readonly MechanicId[]): void {
  for (const id of met) counts[id] = (counts[id] ?? 0) + 1;
}

/**
 * Replay a log and count what it taught.
 *
 * **The backfill**, and the reason 11a is cheap: the log is the save format and
 * `mechanicsOf` is pure, so a profile is a reduction over logs that already
 * exist rather than something that had to have been recorded at the time. A
 * counter added today can be computed over every game already played.
 *
 * Deliberately takes the log rather than a live store, exactly as `scoreLog`
 * does — the same function serves a finished game on disk, a game in progress,
 * and a sweep over many.
 */
export function mechanicsOfLog(log: readonly Command[]): MechanicCounts {
  const counts: MechanicCounts = {};
  let state = newGame();
  for (const command of log) {
    let next: GameState;
    try {
      next = applyCommand(state, command);
    } catch {
      // A log that no longer replays is a bug, but counting what did replay
      // beats returning nothing — the same reading `createRoom` takes.
      break;
    }
    tally(counts, mechanicsOf(state, command, next));
    state = next;
  }
  return counts;
}
